import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FormDialog } from '../../../../../components/form-dialog/form-dialog';
import { Row } from '../../../../../components/row';
import { Cell } from '../../../../../components/cell';
import { MaxLengthTextField } from '../../../../../components/text-field';
import {
  DatePicker,
  DatePickerValue,
} from '../../../../../components/date-picker/date-picker';
import {
  TimePicker,
  TimePickerValue,
} from '../../../../../components/time-picker';
import { UserLookup } from '../../../../../components/lookups/user-lookup/user-lookup';
import { MultiSelect } from '../../../../../components/multi-select/multi-select';
import {
  AssignmentListModel2,
  ObservationDataType,
  ObservationFieldMode,
  ProblemAreaCode,
  ServiceProviderObservationTypeModel,
} from '../../../../../api-service/clients';
import { problemAreaTexts } from '../../health-overview/problem-area-texts';
import { Observation } from './observation-view-model';
import { AssignmentMode } from './observation-assignment-mode';
import { isNumericDataType } from './observation-display-rules';

const problemAreaOptions = Object.values(ProblemAreaCode);

export interface ObservationDialogValues {
  assignmentId?: string;
  observedOn: Date;
  observedByUserId: string;
  numericValue?: number;
  booleanValue?: boolean;
  textValue?: string;
  reflection?: string;
  problemAreaCodes?: ProblemAreaCode[];
}

interface ObservationDialogProps {
  open: boolean;
  isEdit: boolean;
  type: ServiceProviderObservationTypeModel;
  assignments: AssignmentListModel2[];
  initialObservation?: Observation;
  initialObservedOn?: Date;
  initialProblemAreaCodes?: ProblemAreaCode[];
  showProblemAreas?: boolean;
  assignmentMode?: AssignmentMode;
  currentUserId?: string;
  onClose: () => void;
  onSave: (values: ObservationDialogValues) => void;
}

function mostRecentAssignment(assignments: AssignmentListModel2[]): string {
  if (assignments.length === 0) return '';
  const sorted = [...assignments].sort((a, b) => {
    const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
    const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
    return dateB - dateA;
  });
  return sorted[0].assignmentId?.value ?? '';
}

function extractTime(date: Date): TimePickerValue {
  return { hours: date.getHours(), minutes: date.getMinutes() };
}

function combineDateTime(
  date: DatePickerValue,
  time: TimePickerValue
): Date | undefined {
  if (!date || !(date instanceof Date)) return undefined;
  const result = new Date(date);
  if (time && typeof time === 'object' && 'hours' in time) {
    result.setHours(time.hours, time.minutes, 0, 0);
  } else if (typeof time === 'string') {
    const parts = time.split(':');
    if (parts.length === 2) {
      result.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
    }
  }
  return result;
}

export function ObservationDialog({
  open,
  isEdit,
  type,
  assignments,
  initialObservation,
  initialObservedOn,
  initialProblemAreaCodes,
  showProblemAreas = false,
  assignmentMode = 'required',
  currentUserId,
  onClose,
  onSave,
}: ObservationDialogProps) {
  const [assignmentId, setAssignmentId] = useState('');
  const [observedDate, setObservedDate] = useState<DatePickerValue>(new Date());
  const [observedTime, setObservedTime] = useState<TimePickerValue>(undefined);
  const [observedByUserId, setObservedByUserId] = useState<string | undefined>(
    undefined
  );
  const [numericValue, setNumericValue] = useState('');
  const [booleanValue, setBooleanValue] = useState<boolean | undefined>(
    undefined
  );
  const [textValue, setTextValue] = useState('');
  const [reflection, setReflection] = useState('');
  const [problemAreaCodes, setProblemAreaCodes] = useState<ProblemAreaCode[]>(
    []
  );

  const showTime = type.timeFieldMode !== ObservationFieldMode.Hide;
  const timeRequired =
    type.timeFieldMode === ObservationFieldMode.ShowMandatory;
  const showReflection = type.reflectionFieldMode !== ObservationFieldMode.Hide;
  const reflectionRequired =
    type.reflectionFieldMode === ObservationFieldMode.ShowMandatory;

  useEffect(() => {
    if (!open) return;

    if (isEdit && initialObservation) {
      setAssignmentId(initialObservation.assignmentId ?? '');
      setObservedDate(initialObservation.observedOn);
      setObservedTime(extractTime(initialObservation.observedOn));
      setObservedByUserId(initialObservation.observedByUserId);
      setNumericValue(
        initialObservation.numericValue !== undefined
          ? String(initialObservation.numericValue)
          : ''
      );
      setBooleanValue(initialObservation.booleanValue);
      setTextValue(initialObservation.textValue ?? '');
      setReflection(initialObservation.reflection ?? '');
      setProblemAreaCodes(initialObservation.problemAreaCodes);
    } else {
      const observedOn = initialObservedOn ?? new Date();
      setAssignmentId(
        assignmentMode === 'optional' ? '' : mostRecentAssignment(assignments)
      );
      setObservedDate(observedOn);
      setObservedTime(extractTime(observedOn));
      setObservedByUserId(currentUserId);
      setNumericValue('');
      setBooleanValue(undefined);
      setTextValue('');
      setReflection('');
      setProblemAreaCodes(initialProblemAreaCodes ?? []);
    }
  }, [
    open,
    isEdit,
    initialObservation,
    initialObservedOn,
    initialProblemAreaCodes,
    currentUserId,
    assignments,
    assignmentMode,
  ]);

  function numericParsed(): number | undefined {
    if (!isNumericDataType(type.dataType)) return undefined;
    const parsed =
      type.dataType === ObservationDataType.Decimal
        ? parseFloat(numericValue.replace(',', '.'))
        : parseInt(numericValue, 10);
    return isNaN(parsed) ? undefined : parsed;
  }

  function numericError(): string | undefined {
    if (!isNumericDataType(type.dataType) || !numericValue) return undefined;
    const val = numericParsed();
    if (val === undefined) return 'Ugyldig talværdi';
    if (type.minValue !== undefined && val < type.minValue)
      return `Minimum er ${type.minValue}`;
    if (type.maxValue !== undefined && val > type.maxValue)
      return `Maksimum er ${type.maxValue}`;
    return undefined;
  }

  function isValid(): boolean {
    if (!isEdit && assignmentMode === 'required' && !assignmentId) return false;
    if (!(observedDate instanceof Date) || isNaN(observedDate.getTime()))
      return false;
    if (timeRequired && !observedTime) return false;
    if (!observedByUserId) return false;

    if (isNumericDataType(type.dataType)) {
      if (!numericValue) return false;
      if (numericError() !== undefined) return false;
    } else if (type.dataType === ObservationDataType.Boolean) {
      if (booleanValue === undefined) return false;
    } else if (type.dataType === ObservationDataType.StandardTextDropdown) {
      if (!textValue) return false;
    }
    // Text type: optional value (no hard requirement)

    if (reflectionRequired && !reflection.trim()) return false;

    return true;
  }

  function handleSave() {
    if (!isValid()) return;
    const combined = showTime
      ? combineDateTime(observedDate, observedTime)
      : observedDate instanceof Date
      ? observedDate
      : undefined;
    if (!combined) return;

    const values: ObservationDialogValues = {
      assignmentId: assignmentId || undefined,
      observedOn: combined,
      observedByUserId: observedByUserId!,
      numericValue: isNumericDataType(type.dataType)
        ? numericParsed()
        : undefined,
      booleanValue:
        type.dataType === ObservationDataType.Boolean
          ? booleanValue
          : undefined,
      textValue:
        type.dataType === ObservationDataType.Text ||
        type.dataType === ObservationDataType.StandardTextDropdown
          ? textValue || undefined
          : undefined,
      reflection: reflection.trim() || undefined,
      problemAreaCodes: showProblemAreas ? problemAreaCodes : undefined,
    };

    onSave(values);
  }

  const numericErr = numericError();

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Rediger observation' : 'Opret observation'}
      confirmButtonText={isEdit ? 'Opdater' : 'Opret'}
      cancelButtonText="Afbryd"
      isValid={isValid()}
      onConfirmClick={handleSave}
      onCancelClick={onClose}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <Box mt={1} display="flex" flexDirection="column" gap={2}>
        <Row>
          <Cell size="full">
            <Typography variant="body2" color="text.secondary">
              <strong>Type:</strong> {type.name}
            </Typography>
          </Cell>
        </Row>

        <Row>
          <Cell size="full">
            <FormControl
              fullWidth
              variant="outlined"
              required={!isEdit && assignmentMode === 'required'}
              // An update never changes the bestilling, so it is only shown here.
              disabled={isEdit}
            >
              <InputLabel shrink>Bestilling</InputLabel>
              <Select
                value={assignmentId}
                label="Bestilling"
                onChange={e => setAssignmentId(e.target.value)}
                displayEmpty
                notched
              >
                {(assignmentMode === 'optional' ||
                  (isEdit && !assignmentId)) && (
                  // A blank row (the non-breaking space keeps its height), named for screen readers.
                  <MenuItem value="" aria-label="Ingen bestilling">
                    &nbsp;
                  </MenuItem>
                )}
                {assignments.map(a => (
                  <MenuItem
                    key={a.assignmentId?.value}
                    value={a.assignmentId?.value ?? ''}
                  >
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
              {!isEdit &&
                assignmentMode === 'required' &&
                assignments.length === 0 && (
                  <FormHelperText>
                    Borgeren har ingen aktuel bestilling hos udførerenheden, så
                    observationen kan ikke registreres.
                  </FormHelperText>
                )}
            </FormControl>
          </Cell>
        </Row>

        <Row>
          <Cell size={showTime ? 'half' : 'full'}>
            <DatePicker
              label="Dato"
              value={observedDate}
              onChange={setObservedDate}
              required
              fullWidth
              variant="outlined"
            />
          </Cell>
          {showTime && (
            <Cell size="half">
              <TimePicker
                label="Kl."
                value={observedTime}
                onChange={setObservedTime}
                required={timeRequired}
                fullWidth
                variant="outlined"
              />
            </Cell>
          )}
        </Row>

        <Row>
          <Cell size="full">
            <UserLookup
              label="Observeret af"
              value={observedByUserId}
              onChange={userId => setObservedByUserId(userId)}
              required
              variant="outlined"
            />
          </Cell>
        </Row>

        <Row>
          <Cell size="full">
            {isNumericDataType(type.dataType) && (
              <MaxLengthTextField
                label={`Observation${type.unit ? ` (${type.unit})` : ''}`}
                maxLength={20}
                maxLengthError="Må ikke overskride 20 tegn"
                value={numericValue}
                onChange={e => setNumericValue(e.currentTarget.value)}
                fullWidth
                variant="outlined"
                required
                inputProps={{
                  inputMode:
                    type.dataType === ObservationDataType.Decimal
                      ? 'decimal'
                      : 'numeric',
                }}
                error={!!numericErr}
                helperText={numericErr}
              />
            )}
            {type.dataType === ObservationDataType.Boolean && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Observation
                </Typography>
                <ToggleButtonGroup
                  value={
                    booleanValue === undefined
                      ? null
                      : booleanValue
                      ? 'ja'
                      : 'nej'
                  }
                  exclusive
                  onChange={(_, val) => {
                    if (val === 'ja') setBooleanValue(true);
                    else if (val === 'nej') setBooleanValue(false);
                  }}
                  size="small"
                >
                  <ToggleButton value="ja">Ja</ToggleButton>
                  <ToggleButton value="nej">Nej</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            {type.dataType === ObservationDataType.StandardTextDropdown && (
              <FormControl fullWidth variant="outlined" required>
                <InputLabel shrink>Observation</InputLabel>
                <Select
                  value={textValue}
                  label="Observation"
                  onChange={e => setTextValue(e.target.value)}
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>Vælg</em>
                  </MenuItem>
                  {(type.standardTexts ?? []).map(t => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {type.dataType === ObservationDataType.Text && (
              <MaxLengthTextField
                label="Observation"
                maxLength={4000}
                maxLengthError="Må ikke overskride 4000 tegn"
                value={textValue}
                onChange={e => setTextValue(e.currentTarget.value)}
                fullWidth
                variant="outlined"
                multiline
                minRows="3"
              />
            )}
          </Cell>
        </Row>

        {showProblemAreas && (
          <Row>
            <Cell size="full">
              <MultiSelect<ProblemAreaCode>
                label="Sygeplejefaglige problemområder"
                value={problemAreaCodes}
                options={problemAreaOptions}
                getOptionLabel={code => problemAreaTexts[code].label}
                onChange={setProblemAreaCodes}
                variant="outlined"
              />
            </Cell>
          </Row>
        )}

        {showReflection && (
          <Row>
            <Cell size="full">
              <MaxLengthTextField
                label="Refleksion"
                maxLength={5000}
                maxLengthError="Må ikke overskride 5000 tegn"
                value={reflection}
                onChange={e => setReflection(e.currentTarget.value)}
                fullWidth
                variant="outlined"
                multiline
                minRows="3"
                required={reflectionRequired}
              />
            </Cell>
          </Row>
        )}
      </Box>
    </FormDialog>
  );
}
